import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Input, Row, Select, Space, Typography } from 'antd';
import { SaveOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import EmployeeProfilePanels from '../admin/EmployeeProfilePanels';
import './MyProfilePage.scss';

const { Title, Text } = Typography;

const ROLE_OPTIONS = [
  { value: 'ROLE_EMPLOYEE', label: 'Employee' },
  { value: 'ROLE_FWORKER', label: 'Field Worker' },
  { value: 'ROLE_MANAGER', label: 'Manager' },
  { value: 'ROLE_CMANAGER', label: 'Company Manager' },
  { value: 'ROLE_FMANAGER', label: 'Finance Manager' },
  { value: 'ROLE_SADMIN', label: 'Super Admin' },
];

function getCurrentEmployeeId(authContext) {
  const info = authContext.additionalUserInfo || {};
  return info.id || info.userId || info.employeeId || info.staffId || authContext.user?.userId || authContext.user?.username || null;
}

function getCompanyId(info) {
  return info?.companyId || info?.company?.companyId || info?.company?.id || null;
}

export default function MyProfilePage() {
  const authContext = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const token = authContext.token;
  const role = authContext.role;
  const isUserManager = role === 'ROLE_MANAGER' || role === 'ROLE_CMANAGER';

  const [employee, setEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const employeeId = useMemo(() => getCurrentEmployeeId(authContext), [authContext]);

  useEffect(() => {
    const info = authContext.additionalUserInfo || {};
    if (!employeeId) return;

    const hydrated = {
      ...info,
      id: employeeId,
      phoneNumber: info.phoneNumber || info.phone || '',
    };

    setEmployee(hydrated);
    form.setFieldsValue({
      firstName: hydrated.firstName || '',
      lastName: hydrated.lastName || '',
      email: hydrated.email || '',
      phoneNumber: hydrated.phoneNumber || '',
      role: hydrated.role || role || 'ROLE_EMPLOYEE',
      jobTitle: hydrated.jobTitle || '',
      department: hydrated.department || '',
      xeroEmployeeId: hydrated.xeroEmployeeId || '',
    });
  }, [authContext.additionalUserInfo, employeeId, form, role]);

  // Reusable guard for saveuser
  function canSaveUser(payload) {
    const id = payload?.id || payload?.userId || payload?.employeeId;
    const role = payload?.role;
    return !!id && !!role;
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const companyId = getCompanyId(authContext.additionalUserInfo);
      const payload = {
        ...employee,
        ...values,
        phone: values.phoneNumber || '',
        companyId,
      };

      if (!canSaveUser(payload)) {
        setSaveError('Cannot save: User ID or role is missing.');
        setSaving(false);
        return;
      }

      const response = await fetch(`${baseUrl_SPB}/api/v1/user/saveuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Save failed');
      }

      const updated = { ...employee, ...values, phone: values.phoneNumber || '' };
      setEmployee(updated);
      authContext.setAdditionalUserInfo?.((prev) => ({ ...prev, ...values, phone: values.phoneNumber || '' }));
      setSaveSuccess('Profile updated successfully.');
    } catch (err) {
      if (err?.errorFields) return;
      setSaveError('Failed to save your profile. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!employeeId) {
    return <Alert type="warning" showIcon message="Unable to resolve your profile." />;
  }

  return (
    <div className="my-profile-page">
      <div className="my-profile-hero">
        <div className="my-profile-hero__glow my-profile-hero__glow--one" />
        <div className="my-profile-hero__glow my-profile-hero__glow--two" />
        <div className="my-profile-hero__content">
          <Text className="my-profile-overline">Employee workspace</Text>
          <Title level={2} className="my-profile-title">My Profile</Title>
          <Text className="my-profile-subtitle">
            Update your personal details and manage the same profile panels available in Manage Employee.
          </Text>
          <Space className="my-profile-hero__actions" wrap>
            <Button icon={<UserOutlined />} onClick={() => navigate('/onboarding/me')}>
              Open onboarding
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Save profile
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={10} className="my-profile-column my-profile-column--form">
          <Card className="my-profile-card" title="Basic Information">
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {saveError && <Alert type="error" showIcon message={saveError} />}
              {saveSuccess && <Alert type="success" showIcon message={saveSuccess} />}

              <Form form={form} layout="vertical">
                <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'Required' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Required' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
                  <Input disabled />
                </Form.Item>
                <Form.Item name="phoneNumber" label="Phone number">
                  <Input />
                </Form.Item>
                <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Required' }]}>
                  <Select options={ROLE_OPTIONS} disabled />
                </Form.Item>
                <Form.Item name="jobTitle" label="Job title">
                  <Input placeholder="e.g. Electrician" />
                </Form.Item>
                <Form.Item name="department" label="Department">
                  <Input placeholder="e.g. Operations" />
                </Form.Item>
                <Form.Item name="xeroEmployeeId" label="Xero ID">
                  <Input placeholder="e.g. XERO12345" />
                </Form.Item>
              </Form>

              <Text type="secondary" style={{ fontSize: 12 }}>
                ID: {employee?.id || employeeId}
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={14} className="my-profile-column my-profile-column--panels">
          <Card className="my-profile-card" title="Profile Management">
            <div className="my-profile-panels">
              {employee && (
                <EmployeeProfilePanels
                  employee={employee}
                  token={token}
                  isUserManager={isUserManager}
                  onDataUpdate={() => {}}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
